import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { finalize, first, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { EmploymentAgreementProcessStep } from 'src/app/common/enums/employment-agreement-process-step';
import { EmploymentAgreementBasicInformationRequest } from 'src/app/contracts/requests/employment-agreement-basic-information-request';
import { EmploymentAgreementService } from 'src/app/data/employment-agreement.service';
import { BasicInformationBaseComponent } from 'src/app/base-components/basic-information-base.component';
import { LegalizationDataForWorkerAgreementDto } from 'src/app/models/dtos/worker-agreements/legalization-data-for-worker-agreement-dto';
import { combineLatest, Observable, of } from 'rxjs';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { Validators } from '@angular/forms';
import { NewContractDurationUnit } from '../../../models/enums/new-contract-duration-unit.enum';

@Component({
  selector: 'app-basic-information-step',
  templateUrl: './basic-information-step.component.html',
  styleUrls: ['./basic-information-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BasicInformationStepComponent extends BasicInformationBaseComponent implements OnInit, OnChanges, OnDestroy {
  private employmentAgreementService = inject(EmploymentAgreementService);
  readonly contractDurationUnitEnum = NewContractDurationUnit;

  listOfRecruiters$: Observable<DictionaryItem[]> = this.dictionaryService.getRecruiters();
  recruiterModuleEnabled = false;
  recruiterIdActive = false;

  isForeigner = false;
  workerAgreementLegalizationData: LegalizationDataForWorkerAgreementDto | null = null;
  private initialized = false;

  get isSubmitForLegalizationEnabled(): boolean {
    const formData = this.basicInformationStepFormGroup.getRawValue();
    return !!(formData.company && formData.employer && formData.employerObject);
  }

  constructor() {
    super();

    this.employmentTypeId = this.route.snapshot.params.employmentTypeId;
  }

  get isExtension() {
    return this.prefetchedAgreement?.IsExtension;
  }

  get isPlannedPeriodSectionVisible(): boolean {
    const employmentDateFrom = this.basicInformationStepFormGroup.get('employmentDateFrom')?.value;
    const employmentDateTo = this.basicInformationStepFormGroup.get('employmentDateTo')?.value;
    const maxDurationMonths = this.maxDurationMonths ?? null;

    return this.isPlannedPeriodSectionVisibleForDates(employmentDateFrom, employmentDateTo, maxDurationMonths);
  }

  async ngOnInit(): Promise<void> {
    await this.handleSubscriptionModules();
    this.getCurrentSupervisor(this.workerId);
    this.onAgreementTypeChange();
    this.onCompanyChange();
    this.onLocationChange();
    this.onSupervisorChange();
    this.onTemporaryWorkplaceChange();
    this.onJobTitleChange();
    this.initEmployerAutoCompletes();
    this.onBasicInformationStepFormGroupChange();
    this.onEmployerChange();
    await this.initRecruiter();
    this.onSupervisorJobTitleChange();
    this.initializePlannedPeriodControls();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onPrefetchedAgreementChanges(changes);
    this.recalculatePlannedPeriodState();

    if (!!this.prefetchedAgreement && !this.initialized) {
      this.initialized = true;
    }
    this.loadOrderItems(this.prefetchedAgreement?.EmployerId, this.prefetchedAgreement?.EmployerObjectId, this.prefetchedAgreement?.OrderItemId);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  private async initRecruiter(): Promise<void> {
    this.recruiterModuleEnabled = await this.modulePermissionService.isAllowedModule(this.moduleNames.Recruiters);
    this.realizeRecruiterOnEmployerChange();
  }

  realizeRecruiterOnEmployerChange() {
    combineLatest([this.listOfEmployers$, this.employer.valueChanges.pipe(startWith(this.employer.value))])
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(([employers, employerId]) => {
          const selectedEmployer = employers.find((p) => p.Id == employerId);

          if (!selectedEmployer?.Id) {
            return of({ hasAccess: false, recruiter: null });
          }

          const hasAccess$ = this.recruiterModuleEnabled ? this.employerService.hasAccessToRecruiter(selectedEmployer.Id) : of(false);
          const recruiter$ = !this.prefetchedAgreement?.Id ? this.workerService.getNewestRecruiter(this.workerId, selectedEmployer.Id) : of(null);

          return combineLatest([hasAccess$, recruiter$]).pipe(
            map(([hasAccess, recruiter]) => ({ hasAccess, recruiter })));
        }),
      )
      .subscribe(({ hasAccess, recruiter }) => {
        this.updateRecruiterFieldBasedOnAccess(hasAccess);

        if (recruiter?.Id) {
          this.recruiter.setValue(recruiter.Id);
          this.recruiter.updateValueAndValidity();
        }
      });
  }

  private updateRecruiterFieldBasedOnAccess(hasAccess: boolean): void {
    this.recruiterIdActive = hasAccess;

    if (!hasAccess) {
      this.recruiter.setValue(null);
      this.recruiter.clearValidators();
    } else {
      this.recruiter.setValidators(Validators.required);
    }

    this.recruiter.updateValueAndValidity();
  }

  submit() {
    this.normalizePlannedPeriodFieldsForSubmit();
    this.basicInformationStepFormGroup.updateValueAndValidity({ emitEvent: false });
    this.basicInformationStepFormGroup.markAllAsTouched();
    if (this.basicInformationStepFormGroup.invalid || (!this.agreementId && this.basicInformationStepFormGroup.pristine)) return;

    this.spinner.show();

    const action$ = !this.agreementId
      ? this.employmentAgreementService.createEmploymentAgreementBasicInformation(
          this.workerId,
          this.employmentTypeId,
          this.createRequest(),
          this.maxDurationMonths ?? null,
        )
      : this.employmentAgreementService.updateEmploymentAgreementBasicInformation(
        this.workerId,
        this.employmentTypeId,
        this.agreementId,
        this.createRequest(),
        this.maxDurationMonths ?? null,
      );

    action$
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((id) => {
        if (!this.agreementId) {
          this.router.navigate(['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', id], {
            queryParams: { step: EmploymentAgreementProcessStep.WageParameters },
          });
        }
        this.basicInformationSaved.emit();
        this.formGroupChange.emit(this.updateBasicInformationStep(this.basicInformationStepFormGroup.getRawValue()));
      });
  }

  private createRequest(): EmploymentAgreementBasicInformationRequest {
    const value = this.basicInformationStepFormGroup.getRawValue();
    const shouldSendPlannedPeriod = this.isPlannedPeriodSectionVisible;

    return {
      CompanyId: value.company,
      EmployerId: value.employer,
      EmployerObjectId: value.employerObject?.Id,
      LocationId: value.location?.Id,
      PotentialSupervisorId: value.supervisor?.Id,
      AgreementTypeId: value.agreementType,
      ConclusionDate: value.conclusionDate,
      EmploymentDateFrom: value.employmentDateFrom,
      EmploymentDateTo: value.employmentDateTo,
      NewContractDurationValue: shouldSendPlannedPeriod ? value.contractDurationValue : null,
      NewContractDurationUnit: shouldSendPlannedPeriod ? value.contractDurationUnit : null,
      OrganizationalUnitId: value.organizationalUnit?.Id,
      JobTitleId: value.jobTitle,
      Responsibilities: value.responsibilities.map((res: DictionaryItem) => res.Id),
      TemporaryWorkplaceId: value.temporaryWorkplace?.Id,
      TemporaryWorkplaceName: typeof value.temporaryWorkplace === 'object' ? value.temporaryWorkplace.Name : value.temporaryWorkplace,
      OrderItemId: value.orderItem?.Id,
      RecruiterId: value.recruiter,
      SupervisorJobTitleId: value.supervisorJobTitle
    };
  }

  private normalizePlannedPeriodFieldsForSubmit(): void {
    if (this.isPlannedPeriodSectionVisible) {
      this.contractDurationValue.enable({ emitEvent: false });
      this.contractDurationUnit.enable({ emitEvent: false });
      return;
    }

    this.contractDurationValue.setValue(null, { emitEvent: false });
    this.contractDurationUnit.setValue(null, { emitEvent: false });
    this.contractDurationValue.setErrors(null);
    this.contractDurationUnit.setErrors(null);
  }
}
